import { ConfirmationAlertTestID, TextTestID } from "@/constants/TestID";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import type { FAIconName } from "@/ui/core";
import { Button, Text } from "@/ui/core";
import type { ButtonVariant } from "@/ui/core/buttons/Button";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

type ConfirmationAlertType = "destructive" | "neutral";

interface ConfirmationAlertProps extends ViewWithTestID {
  confirmationType: ConfirmationAlertType;
  confirmLeftIcon?: FAIconName;
  confirmRightIcon?: FAIconName;
  confirmText: string;
  // stretches the confirm button to fill the sheet width instead of sizing to its content
  isConfirmFullWidth?: boolean;
  isOpen: boolean;
  message: string;
  onConfirm: () => void;
  onDismiss: () => void;
  title: string;
}

const ConfirmationAlert = ({
  confirmationType,
  confirmLeftIcon,
  confirmRightIcon,
  confirmText,
  isConfirmFullWidth,
  isOpen,
  message,
  onConfirm,
  onDismiss,
  testID,
  title,
}: ConfirmationAlertProps) => {
  const baseTestID = joinTestIDs(testID, ConfirmationAlertTestID.Primary);
  const buttonVariant: ButtonVariant =
    confirmationType === "destructive" ? "accent.critical" : "primary";

  return (
    <BottomSheetModal
      contentContainerGap={12}
      dismissAccessibilityLabel="Dismiss Confirmation Alert"
      isOpen={isOpen}
      onRequestClose={onDismiss}
      testID={baseTestID}
      title={title}
    >
      <VStack gap={18}>
        <Text
          testID={joinTestIDs(baseTestID, `${TextTestID.Description} `)}
          variant="body"
        >
          {message}
        </Text>
        {isConfirmFullWidth ? (
          <Button
            isFullWidth
            leftIcon={confirmLeftIcon}
            onPress={onConfirm}
            rightIcon={confirmRightIcon}
            testID={joinTestIDs(baseTestID, "confirm")}
            title={confirmText}
            variant={buttonVariant}
          />
        ) : (
          <HStack justifyContent="center">
            <Button
              leftIcon={confirmLeftIcon}
              onPress={onConfirm}
              rightIcon={confirmRightIcon}
              testID={joinTestIDs(baseTestID, "confirm")}
              title={confirmText}
              variant={buttonVariant}
            />
          </HStack>
        )}
      </VStack>
    </BottomSheetModal>
  );
};

export type { ConfirmationAlertProps, ConfirmationAlertType };
export default ConfirmationAlert;
